import { Link2Icon } from '@radix-ui/react-icons';
import type { Editor } from '@tiptap/react';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import type { toggleVariants } from '~shared/shadcn/ui/toggle';

import {
  applyEditorLink,
  type EditorSelectionRange,
} from '../../lib/apply-editor-link';
import { ToolbarButton } from '../toolbar-button';
import { LinkEditBlock } from './link-edit-block';

interface LinkEditPopoverProps extends VariantProps<typeof toggleVariants> {
  editor: Editor;
}

const LinkEditPopover = ({ editor, size, variant }: LinkEditPopoverProps) => {
  const [open, setOpen] = React.useState(false);
  const savedSelection = React.useRef<EditorSelectionRange | null>(null);
  const [defaultText, setDefaultText] = React.useState('');

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        const { from, to } = editor.state.selection;
        savedSelection.current = { from, to };
        setDefaultText(editor.state.doc.textBetween(from, to, ' '));
      } else {
        savedSelection.current = null;
      }
      setOpen(nextOpen);
    },
    [editor],
  );

  const onSetLink = React.useCallback(
    (url: string, text?: string, openInNewTab?: boolean) => {
      applyEditorLink(editor, url, {
        text,
        openInNewTab,
        selection: savedSelection.current ?? undefined,
      });
      setOpen(false);
    },
    [editor],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <ToolbarButton
          isActive={editor.isActive('link')}
          tooltip='Link'
          aria-label='Insert link'
          disabled={editor.isActive('codeBlock')}
          size={size}
          variant={variant}
        >
          <Link2Icon className='size-5' />
        </ToolbarButton>
      </PopoverTrigger>
      <PopoverContent
        className='w-full min-w-80'
        align='end'
        side='bottom'
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        onInteractOutside={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest('.minimal-tiptap-editor')) {
            event.preventDefault();
          }
        }}
      >
        <LinkEditBlock
          key={
            open
              ? `${savedSelection.current?.from ?? 0}-${savedSelection.current?.to ?? 0}`
              : 'closed'
          }
          onSave={onSetLink}
          defaultText={defaultText}
        />
      </PopoverContent>
    </Popover>
  );
};

export { LinkEditPopover };
